import { motion } from 'framer-motion';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AnimationShell, Button, Typography, CustomMarkdown } from '@/components';

import { ServiceDetails } from '../../types';

export type ServiceItemsProps = {
  readonly service: ServiceDetails;
};

export function ServiceItems({ service }: ServiceItemsProps) {
  const { t } = useTranslation(['services']);

  const [openSet, setOpenSet] = useState<Set<number>>(() => new Set([0]));

  const toggle = useCallback((idx: number) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const isOpen = (idx: number) => openSet.has(idx);

  const animationCollapseVariants = {
    open: { opacity: 1, height: 'auto' },
    closed: { opacity: 0, height: 0 },
  };

  return (
    <section className="">
      <AnimationShell type="slide-left" className="flex flex-col">
        <Typography as="h4" size="heading-2" color="primary" weight="semibold" className="mb-6">
          {t('Services')}
        </Typography>

        <div className="divide-y">
          {service.services.map((item, index) => {
            const panelId = `svc-panel-${index}`;
            const headerId = `svc-header-${index}`;
            const open = isOpen(index);

            return (
              <div key={panelId} className="border">
                <div className="flex justify-between hover:cursor-pointer" onClick={() => toggle(index)}>
                  <Typography as="h5" size="body-1" color="primary" weight="semibold" className="p-4">
                    {item.title}
                  </Typography>

                  <Button
                    size="md"
                    color="link"
                    icon={open ? 'minus' : 'plus'}
                    className="pointer-events-none z-50 p-4"
                  ></Button>
                </div>
                <motion.div
                  id={panelId}
                  role="region"
                  aria-labelledby={headerId}
                  key={open ? `open-${index}` : `closed-${index}`}
                  variants={animationCollapseVariants}
                  initial="closed"
                  animate={open ? 'open' : 'closed'}
                  exit="closed"
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <CustomMarkdown text={item.description} className="p-4" />
                </motion.div>
              </div>
            );
          })}
        </div>
      </AnimationShell>
    </section>
  );
}
